import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardCheck, 
  Thermometer, 
  Package,
  ListTodo,
  Wallet,
  TrendingUp,
  Calculator,
  LineChart,
  Users,
  Shield,
  MessageSquare,
  Smartphone,
  Globe,
  Building2,
  ArrowRight,
  ShoppingBag,
  Receipt
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const Features = () => {
  const { t } = useTranslation();

  const operationsFeatures = [
    {
      icon: ClipboardCheck,
      title: t('features.operations.checklists.title'),
      description: t('features.operations.checklists.description'),
    },
    {
      icon: Thermometer,
      title: t('features.operations.haccp.title'),
      description: t('features.operations.haccp.description'),
    },
    {
      icon: Package,
      title: t('features.operations.stock.title'),
      description: t('features.operations.stock.description'),
    },
    {
      icon: ListTodo,
      title: t('features.operations.dailyTasks.title'),
      description: t('features.operations.dailyTasks.description'),
    },
  ];

  const financeFeatures = [
    {
      icon: Wallet,
      title: t('features.finance.cashCounts.title'),
      description: t('features.finance.cashCounts.description'),
    },
    {
      icon: TrendingUp,
      title: t('features.finance.margins.title'),
      description: t('features.finance.margins.description'),
    },
    {
      icon: Calculator,
      title: t('features.finance.costs.title'),
      description: t('features.finance.costs.description'),
    },
    {
      icon: LineChart,
      title: t('features.finance.forecast.title'),
      description: t('features.finance.forecast.description'),
    },
    {
      icon: ShoppingBag,
      title: t('features.finance.products.title'),
      description: t('features.finance.products.description'),
    },
    {
      icon: Receipt,
      title: t('features.finance.costPrice.title'),
      description: t('features.finance.costPrice.description'),
    },
  ];

  const teamFeatures = [
    {
      icon: Shield,
      title: t('features.team.permissions.title'),
      description: t('features.team.permissions.description'),
    },
    {
      icon: Users,
      title: t('features.team.reviews.title'),
      description: t('features.team.reviews.description'),
    },
    {
      icon: MessageSquare,
      title: t('features.team.communication.title'),
      description: t('features.team.communication.description'),
    },
  ];

  const technicalFeatures = [
    {
      icon: Smartphone,
      title: t('features.technical.pwa.title'),
      description: t('features.technical.pwa.description'),
    },
    {
      icon: Globe,
      title: t('features.technical.languages.title'),
      description: t('features.technical.languages.description'),
    },
    {
      icon: Building2,
      title: t('features.technical.multiBranch.title'),
      description: t('features.technical.multiBranch.description'),
    },
  ];

  const FeatureCard = ({ icon: Icon, title, description }) => (
    <div className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#7876bb]/30 hover:shadow-lg transition-all duration-300 group">
      <div className="w-12 h-12 bg-[#7876bb]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#7876bb] transition-colors">
        <Icon className="w-6 h-6 text-[#7876bb] group-hover:text-white transition-colors" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );

  const FeatureSection = ({ title, description, features, imagePosition = 'right', imageSrc }) => (
    <div className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid lg:grid-cols-2 gap-12 items-center ${imagePosition === 'left' ? 'lg:flex-row-reverse' : ''}`}>
          <div className={imagePosition === 'left' ? 'lg:order-2' : ''}>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
            <p className="text-lg text-gray-600 mb-8">{description}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </div>
          </div>
          <div className={imagePosition === 'left' ? 'lg:order-1' : ''}>
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src={imageSrc}
                alt={title}
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#7876bb]/10 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#7876bb]/5 via-white to-[#7876bb]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('features.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('features.subtitle')}
          </p>
        </div>
      </section>

      {/* Operations Section */}
      <FeatureSection
        title={t('features.operations.title')}
        description={t('features.operations.description')}
        features={operationsFeatures}
        imageSrc="https://images.unsplash.com/photo-1571342174342-8f276883484b?w=600&q=80"
        imagePosition="right"
      />

      {/* Finance Section */}
      <div className="bg-gray-50">
        <FeatureSection
          title={t('features.finance.title')}
          description={t('features.finance.description')}
          features={financeFeatures}
          imageSrc="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80"
          imagePosition="left"
        />
      </div>

      {/* Team Section */}
      <FeatureSection
        title={t('features.team.title')}
        description={t('features.team.description')}
        features={teamFeatures}
        imageSrc="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80"
        imagePosition="right"
      />

      {/* Technical Excellence */}
      <section className="py-20 bg-gradient-to-br from-[#7876bb]/10 via-[#7876bb]/5 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('features.technical.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('features.technical.description')}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {technicalFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-[#7876bb]/20 hover:border-[#7876bb]/40 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-[#7876bb]/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#7876bb]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('cta.subtitle')}
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-[#7876bb] rounded-xl hover:bg-[#6665a8] transition-colors shadow-lg shadow-[#7876bb]/30"
          >
            {t('cta.button')}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Features;